import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { FolderArrowDownIcon, FolderIcon } from "@heroicons/react/24/solid";
import { NOTIFICATION } from "@via/context/notifications-context.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { TREE_FILE_MESSAGES } from "@via/utils/constants.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import { type TreeDataScriptInterface } from "@via/utils/interfaces.tsx";
import { type JSX } from "react";
import { type NodeRendererProps } from "react-arborist";

const TreeNode = ({
  node,
  style,
  tree,
}: NodeRendererProps<TreeDataScriptInterface>): JSX.Element => {
  const { setNotification } = useNotifications();

  const isEmptyFileName = (name: string): boolean => {
    return name === "";
  };

  const isDuplicateFileName = (name: string): boolean | undefined => {
    return node?.parent?.children?.some(
      item =>
        item.data.id !== node.data.id &&
        item.data.name.toLowerCase() === name.toLowerCase()
    );
  };

  const handleSubmit = (input): void => {
    const name = input.value.trim();

    // Use a function to determine the type of notification detail required, if any.
    const getNotificationDetail = (): string => {
      if (isEmptyFileName(name)) {
        return TREE_FILE_MESSAGES.FILENAME_REQUIRED;
      }
      if (isDuplicateFileName(name)) {
        return TREE_FILE_MESSAGES.DUPLICATE_FILES;
      }
      return "";
    };

    const notificationDetail = getNotificationDetail();

    // Set the notification if there's a detail message; otherwise, submit the node.
    if (notificationDetail) {
      setNotification({
        id: "tree-file-invalid-files",
        message: TREE_FILE_MESSAGES.INVALID_FILES,
        details: notificationDetail,
        variant: NOTIFICATION.ERROR,
      });
    } else {
      node.submit(name);
    }
  };

  const handleBlur = (): void => {
    if (!node.data.path) {
      tree.delete(node.data.id);
      return;
    }
    node.reset();
    node.focus();
  };

  return (
    <button
      title={node.data.name}
      className={`node-container ${node.isSelected ? "isSelected" : ""}`}
      style={style}>
      <div
        className="node-content"
        onClick={() => {
          node.isInternal && node.toggle();
        }}>
        {node.isLeaf ? (
          <>
            <span className="arrow"></span>
            <span className="file-folder-icon">
              <DocumentTextIcon className="h-4 w-4 text-gray-400" />
            </span>
          </>
        ) : (
          <>
            <span title={node.data.name} className="arrow">
              <ChevronRightIcon
                className={classNames(
                  "page-heading",
                  node.isOpen
                    ? "rotate-90 duration-200 ease-in-out"
                    : "duration-200 ease-in-out"
                )}
              />
            </span>
            {node.level === 0 ? (
              <span className="file-folder-icon">
                <FolderIcon className="h-4 w-4 text-[#83CAF4]" />
              </span>
            ) : (
              <span className="file-folder-icon">
                <FolderArrowDownIcon className="h-4 w-4 text-[#55baf4]" />
              </span>
            )}
          </>
        )}
        <span className="node-text">
          {node.isEditing ? (
            <input
              type="text"
              defaultValue={node.data.name}
              onFocus={e => {
                e.currentTarget.select();
              }}
              onBlur={e => {
                if (e.currentTarget.value === "") {
                  handleBlur();
                } else {
                  handleSubmit(e.currentTarget);
                }
              }}
              onKeyDown={e => {
                if (e.key === "Escape") handleBlur();
                if (e.key === "Enter") handleSubmit(e.currentTarget);
              }}
              autoFocus
            />
          ) : (
            <span>{node.data.name}</span>
          )}
        </span>
      </div>
    </button>
  );
};

export default TreeNode;
